import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard, Wallet, Banknote, FileCheck2, ShieldCheck, Users2, Building2,
  Receipt, ListChecks, ArrowLeftRight, ScrollText, DatabaseBackup, BarChart3, Bell, Menu, X, Trash2,
  LogOut, Sun, Moon, Search as SearchIcon, Sparkles, Settings, BookOpen, ChevronDown,
  Coins, BellRing, PiggyBank, HandCoins, Truck, Briefcase, ClipboardList, Store, CalendarClock,
  ClipboardCheck, KeyRound, HeartPulse, Radar, FileStack
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { api, formatINR, lsGet, lsSet, lsDel } from '../lib/api';
import { getNotifPermission, requestNotifPermission, showBrowserNotification, subscribeToPush } from '../lib/push';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';
import TripTracker from './TripTracker';
import PresenceTracker from './PresenceTracker';
import NativeTrackerSetup from './NativeTrackerSetup';
import { canAccess, homeFor } from '../lib/rbac';
import { AdminLedgerCleanup } from './AdminLedgerCleanup';

const NAV = [
  { to: '/dashboard', label: 'Command Center', icon: LayoutDashboard },
  { to: '/assistant', label: 'Assistant', icon: Sparkles },
  { to: '/payment-requests', label: 'Payments & Approvals', icon: FileCheck2 },
  { to: '/transactions', label: 'Transactions', icon: Receipt },
  { to: '/bank-accounts', label: 'Accounts', icon: Banknote },
  { to: '/daily-collections', label: 'Daily Collections', icon: PiggyBank },
  // Cash Management — disabled per user request (feature intact, re-enable later)
  // { to: '/cash-management', label: 'Cash Management', icon: Coins },
  { to: '/vendors', label: 'Vendors', icon: Building2 },
  { to: '/inter-entity', label: 'Inter-Entity Transfers', icon: ArrowLeftRight },
  { to: '/tasks', label: 'Tasks', icon: ListChecks },
  // Salary Data — temporarily hidden per user request (feature intact, will re-enable later)
  // { to: '/salary', label: 'Salary Data', icon: HandCoins },
  { to: '/reimbursements', label: 'Reimbursements', icon: Receipt },
  { to: '/reporting', label: 'Reporting', icon: FileStack },
  { to: '/store-dashboard', label: 'Store Dashboard', icon: Store },
  { to: '/live-map', label: 'Live Location', icon: Radar },
  { to: '/sale-corrections', label: 'Sale Corrections', icon: ClipboardCheck },
  { to: '/data-health', label: 'Data Health', icon: HeartPulse },
  { to: '/security', label: 'Security', icon: KeyRound },
  { to: '/duty-roster', label: 'Duty Roster', icon: CalendarClock },
  { to: '/staff-hr', label: 'Staff (HR)', icon: Users2 },
  { to: '/directors', label: 'Directors', icon: Briefcase },
  { to: '/vehicles', label: 'Office Vehicles', icon: Truck },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/entities', label: 'Entities', icon: Building2 },
  { to: '/users', label: 'Users & Roles', icon: Users2 },
  { to: '/access-review', label: 'Access Review', icon: ShieldCheck },
  { to: '/audit', label: 'Audit & Access', icon: ScrollText },
  { to: '/forms', label: 'Data Forms', icon: ClipboardList },
  { to: '/backups', label: 'Backups', icon: DatabaseBackup },
  { to: '/records', label: 'Records & Bin', icon: Trash2 },
];

function NavItem({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${to.replace('/','')}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
          isActive
            ? 'bg-accent/15 text-accent font-semibold border border-accent/20'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
        }`
      }
    >
      <Icon size={17} strokeWidth={1.75} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function SidebarContent({ onItemClick }) {
  const { user } = useAuth();
  const filteredNav = NAV.filter(n => user && canAccess(user.role, n.to));
  return (
    <div className="flex h-full flex-col gap-1">
      <div className="px-4 pt-5 pb-4 border-b border-border">
        <Link to={homeFor(user?.role)} className="flex items-center gap-2.5" data-testid="brand-link">
          <img src="/shp-icon.png" alt="Sahakar" className="w-9 h-9 rounded-xl shadow-lg shadow-black/10 dark:shadow-black/30 object-cover"/>
          <div className="flex flex-col leading-tight">
            <span className="font-heading font-bold text-base">Sahakar</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">FMS Solution</span>
          </div>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto thin-scroll px-2.5 py-3 space-y-0.5">
        {filteredNav.map(n => <NavItem key={n.to} {...n} onClick={onItemClick} />)}
      </div>
      <div className="px-3 py-3 border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span className="font-medium">v1.0</span>
          <button
            onClick={() => { lsDel('sahakar_install_snooze');
                             window.dispatchEvent(new Event('sahakar:install-ask')); }}
            className="hover:text-foreground underline decoration-dotted"
            data-testid="install-ask-btn"
          >
            Add to home screen
          </button>
        </div>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!q) { setResults(null); return; }
    const id = setTimeout(async () => {
      try { const { data } = await api.get('/search', { params: { q } }); setResults(data); }
      catch { setResults(null); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const flat = results ? [
    ...(results.payment_requests||[]).map(x => ({ kind: 'Payment', label: `${x.request_number} - ${x.purpose}`, to: `/payment-requests` })),
    ...(results.vendors||[]).map(x => ({ kind: 'Vendor', label: x.name, to: '/vendors' })),
    ...(results.entities||[]).map(x => ({ kind: 'Entity', label: x.name, to: '/entities' })),
    ...(results.bank_accounts||[]).map(x => ({ kind: 'Bank', label: `${x.bank_name} • ${x.account_number}`, to: '/bank-accounts' })),
    ...(results.tasks||[]).map(x => ({ kind: 'Task', label: x.title, to: '/tasks' })),
    ...(results.transactions||[]).map(x => ({ kind: 'Txn', label: `${formatINR(x.amount)} • ${x.description}`, to: '/transactions' })),
  ] : [];

  return (
    <div className="relative flex-1 max-w-md">
      <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input
        data-testid="global-search-input"
        placeholder="Search entities, vendors, requests…"
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onChange={e => setQ(e.target.value)}
        className="pl-9 h-9 bg-muted/40 border-border/60"
      />
      {open && q && flat.length > 0 && (
        <div className="absolute mt-1 w-full bg-popover border border-border rounded-lg shadow-xl z-50 max-h-80 overflow-auto thin-scroll" data-testid="search-results">
          {flat.slice(0, 12).map((r, i) => (
            <button
              key={`${r.to}-${r.label}`}
              onClick={() => { nav(r.to); setQ(''); setResults(null); }}
              className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-3 border-b border-border/40 last:border-b-0"
              data-testid={`search-result-${i}`}
            >
              <Badge variant="secondary" className="text-[10px]">{r.kind}</Badge>
              <span className="text-sm truncate">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [notifs, setNotifs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifPerm, setNotifPerm] = useState('default');
  const seenIds = useRef(new Set());
  const nav = useNavigate();

  useEffect(() => {
    getNotifPermission().then((p) => {
      setNotifPerm(p);
      if (p === 'granted') subscribeToPush().catch(() => null);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    let timer;
    const fetchNotifs = async () => {
      if (document.hidden) return; // don't poll a background tab
      try {
        const r = await api.get('/notifications');
        const items = r.data || [];
        setNotifs(items);
        // Show browser notifications for unseen unread items
        if (notifPerm === 'granted') {
          const newOnes = items.filter(n => !n.read && !seenIds.current.has(n.id));
          if (newOnes.length > 0) {
            newOnes.slice(0, 3).forEach(n => showBrowserNotification(n.title, n.message, n.link));
            const newSeen = new Set(seenIds.current);
            items.forEach(n => newSeen.add(n.id));
            seenIds.current = new Set([...newSeen].slice(-200));
          }
        }
      } catch {}
    };
    fetchNotifs();
    timer = setInterval(fetchNotifs, 30000);
    return () => clearInterval(timer);
  }, [user, notifPerm]);

  const enableNotifs = async () => {
    const perm = await requestNotifPermission();
    setNotifPerm(perm);
  };

  const unread = notifs.filter(n => !n.read).length;

  if (!user) return children;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card/40 pwa-safe-top">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card pwa-safe-top">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent onItemClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl pwa-safe-top">
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 lg:px-6 h-14">
            <Button
              data-testid="mobile-menu-btn"
              variant="ghost" size="icon"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden"
            >
              <Menu size={20} />
            </Button>
            <GlobalSearch />
            <div className="ml-auto flex items-center gap-1.5">
              <Button
                data-testid="theme-toggle"
                variant="ghost" size="icon"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              >
                {theme === 'dark' ? <Sun size={17}/> : <Moon size={17}/>}
              </Button>
              {user.role === 'super_admin' && <AdminLedgerCleanup />}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="notifications-btn" variant="ghost" size="icon" className="relative">
                    <Bell size={17}/>
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-semibold">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    Notifications
                    <button
                      onClick={async () => { await api.post('/notifications/read-all'); setNotifs(n => n.map(x => ({...x, read: true}))); }}
                      className="text-xs text-accent hover:underline"
                      data-testid="mark-all-read"
                    >Mark all read</button>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifPerm !== 'granted' && notifPerm !== 'unsupported' && (
                    <button
                      onClick={enableNotifs}
                      data-testid="enable-push-notifs"
                      className="w-full flex items-center gap-2 p-3 bg-accent/10 hover:bg-accent/15 border-b border-border text-left transition-colors"
                    >
                      <BellRing size={16} className="text-accent shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-accent">Enable browser notifications</div>
                        <div className="text-xs text-muted-foreground">Get alerts for approvals & tasks even when this tab is in background</div>
                      </div>
                    </button>
                  )}
                  {notifs.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</div>}
                  {notifs.slice(0,8).map(n => (
                    <DropdownMenuItem key={n.id} className="flex flex-col items-start py-2 cursor-pointer"
                      onClick={() => { api.post(`/notifications/${n.id}/read`); if (n.link) nav(n.link); }}>
                      <div className="flex items-center gap-2 w-full">
                        <span className={`w-2 h-2 rounded-full ${n.read ? 'bg-muted' : 'bg-accent'}`}/>
                        <span className="font-medium text-sm">{n.title}</span>
                      </div>
                      <span className="text-xs text-muted-foreground line-clamp-2 ml-4">{n.message}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-9 px-2" data-testid="user-menu-btn">
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className="bg-accent/15 text-accent text-xs font-semibold">
                        {(user.name || 'U').split(' ').map(p => p[0]).slice(0,2).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:flex flex-col items-start leading-tight">
                      <span className="text-xs font-semibold">{user.name}</span>
                      <span className="text-[10px] text-muted-foreground capitalize">{(user.role||'').replace('_',' ')}</span>
                    </div>
                    <ChevronDown size={14} className="hidden sm:block"/>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold">{user.name}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav('/users')} data-testid="profile-link">
                    <Settings size={14} className="mr-2"/>Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut size={14} className="mr-2"/>Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto thin-scroll" data-testid="main-content">
          <div className="px-3 sm:px-5 lg:px-8 py-5 lg:py-8 pb-16 max-w-[1600px] mx-auto fade-up">{children}</div>
        </main>
      </div>
      <TripTracker/>
      <PresenceTracker/>
      <NativeTrackerSetup/>
    </div>
  );
}
